import React, { useState } from 'react';
import { filesPlatformAPI } from '@/services';
import { useToast } from './Toast';

/**
 * Bloc « documents complémentaires » aligné sur People (PersonForm) :
 * plusieurs lignes nom + fichier, upload groupé, liste avec retrait.
 */
const SupplementaryDocumentsUploader = ({ documents, onDocumentsChange, disabled }) => {
  const { success, error } = useToast();
  const [drafts, setDrafts] = useState([{ key: `draft_${Date.now()}`, name: '', file: null }]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const uploadToFiles = async (file, customName = '') => {
    const normalizedName = customName.trim();
    const fileToUpload = normalizedName
      ? new File([file], normalizedName, { type: file.type || 'application/octet-stream' })
      : file;
    const uploaded = await filesPlatformAPI.upload(fileToUpload);
    const id = uploaded?.data?.id;
    if (!id) throw new Error('Réponse upload invalide');
    return { id: String(id), filename: uploaded?.data?.filename || normalizedName || file.name };
  };

  const addDraft = () => {
    setDrafts((prev) => [...prev, { key: `draft_${Date.now()}_${prev.length}`, name: '', file: null }]);
  };

  const removeDraft = (key) => {
    setDrafts((prev) => {
      const next = prev.filter((d) => d.key !== key);
      return next.length ? next : [{ key: `draft_${Date.now()}`, name: '', file: null }];
    });
  };

  const updateDraftName = (key, name) => {
    setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, name } : d)));
  };

  const updateDraftFile = (key, file) => {
    setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, file } : d)));
  };

  const uploadDrafts = async () => {
    const filled = drafts.filter((d) => d.name.trim() || d.file);
    if (filled.length === 0) return;
    const invalid = filled.find((d) => !d.name.trim() || !d.file);
    if (invalid) {
      error('Chaque document complémentaire doit avoir un nom affiché et un fichier.');
      return;
    }
    setUploading(true);
    setProgress({ done: 0, total: filled.length });
    try {
      const uploadedBatch = [];
      for (let i = 0; i < filled.length; i++) {
        const draft = filled[i];
        const uploaded = await uploadToFiles(draft.file, draft.name.trim());
        uploadedBatch.push(uploaded);
        setProgress({ done: i + 1, total: filled.length });
      }
      onDocumentsChange([...documents, ...uploadedBatch]);
      setDrafts([{ key: `draft_${Date.now()}`, name: '', file: null }]);
      success(`${uploadedBatch.length} fichier(s) téléversé(s).`);
    } catch (err) {
      error(
        err?.response?.data?.detail ||
          err?.message ||
          'Échec du téléversement (vérifiez le module Files et la taille du fichier).'
      );
    } finally {
      setUploading(false);
      setTimeout(() => setProgress({ done: 0, total: 0 }), 600);
    }
  };

  const removeDocument = (id) => {
    onDocumentsChange(documents.filter((d) => d.id !== id));
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 md:p-5 space-y-4">
      <h3 className="text-sm font-semibold text-gray-900">Documents complémentaires</h3>
      <p className="text-xs text-gray-600">
        Comme sur People : ajoutez une ou plusieurs pièces (nom + fichier), puis téléversez. Les identifiants sont
        ajoutés à <code className="text-xs bg-gray-100 px-1 rounded">document_file_ids</code> avec la photo et la
        pièce d&apos;identité.
      </p>

      <div className="space-y-3">
        {drafts.map((draft, idx) => (
          <div key={draft.key} className="rounded-lg border border-gray-200 p-3 bg-gray-50/80">
            <p className="text-xs text-gray-500 mb-2">Fichier {idx + 1}</p>
            <div className="grid sm:grid-cols-[1fr_auto_auto] gap-2 items-center">
              <input
                type="text"
                value={draft.name}
                onChange={(e) => updateDraftName(draft.key, e.target.value)}
                placeholder="Nom du fichier (ex. acte-naissance.pdf)"
                disabled={disabled || uploading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
              />
              <label className="inline-flex items-center justify-center px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                Choisir
                <input
                  type="file"
                  className="hidden"
                  disabled={disabled || uploading}
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    e.target.value = '';
                    updateDraftFile(draft.key, file);
                  }}
                />
              </label>
              {draft.file && (
                <button
                  type="button"
                  onClick={() => removeDraft(draft.key)}
                  disabled={disabled || uploading}
                  className="px-3 py-2 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 text-sm disabled:opacity-50"
                >
                  Retirer
                </button>
              )}
            </div>
            {draft.file && (
              <p className="text-xs text-gray-500 mt-2">Sélectionné : {draft.file.name}</p>
            )}
          </div>
        ))}

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={addDraft}
            disabled={disabled || uploading}
            className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            + Ajouter un autre fichier
          </button>
          <button
            type="button"
            onClick={uploadDrafts}
            disabled={disabled || uploading}
            className="px-3 py-2 rounded-lg bg-entrea-royal text-white text-sm hover:bg-entrea-navy disabled:opacity-60"
          >
            {uploading ? 'Téléversement…' : 'Téléverser les fichiers saisis'}
          </button>
        </div>

        {progress.total > 0 && (
          <div className="pt-1">
            <p className="text-xs text-gray-600 mb-1">
              Progression : {progress.done}/{progress.total}
            </p>
            <div className="h-2 rounded bg-gray-200 overflow-hidden">
              <div
                className="h-full bg-entrea-royal transition-all"
                style={{
                  width: `${Math.round((progress.done / progress.total) * 100)}%`,
                }}
              />
            </div>
          </div>
        )}
      </div>

      {documents.length > 0 && (
        <ul className="mt-2 space-y-2 border-t border-gray-100 pt-3">
          {documents.map((f) => (
            <li key={f.id} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-gray-800 font-medium">{f.filename}</span>
              <span className="text-gray-500 font-mono text-xs break-all">{f.id}</span>
              <button
                type="button"
                onClick={() => removeDocument(f.id)}
                disabled={disabled || uploading}
                className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
              >
                Retirer
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SupplementaryDocumentsUploader;
